import { getYear } from "date-fns";
import { create } from "zustand";
import type { IArchiveFilter } from "@services/archive/types";
import type { IOverview } from "@services/overview/types";

interface IStatisticsState {
  jointBillServiceIdFilter: number;
  jointBillServiceOptions: { label: string; value: number }[];
  yearFilter: number;
  yearOptions: { label: string; value: number }[];
  serviceProviderPaymentPeriodOffset: number;
  setServiceProviderPaymentPeriodOffset: (
    serviceProviderPaymentPeriodOffset: number,
  ) => void;
  setJointBillServiceIdFilter: (jointBillServiceIdFilter: number) => void;
  setYearFilter: (yearFilter: number) => void;
  updateOptions: (
    overviewData: IOverview,
    archiveFilter: IArchiveFilter,
  ) => void;
  clear: () => void;
}

const initialStore = {
  jointBillServiceIdFilter: 0,
  jointBillServiceOptions: [],
  yearFilter: getYear(new Date()),
  yearOptions: [],
  serviceProviderPaymentPeriodOffset: 1,
};

export const useStatisticsStore = create<IStatisticsState>((set, get) => ({
  ...initialStore,
  setServiceProviderPaymentPeriodOffset: (
    serviceProviderPaymentPeriodOffset,
  ) => {
    set({ serviceProviderPaymentPeriodOffset });
  },
  setJointBillServiceIdFilter: (jointBillServiceIdFilter) => {
    set({ jointBillServiceIdFilter });
  },
  setYearFilter: (yearFilter) => set({ yearFilter }),
  updateOptions: (overviewData, archiveFilter) => {
    const { jointBillServiceIdFilter } = get();

    let yearOptions = [
      ...[
        ...new Set(
          archiveFilter?.jointBillServices
            .filter(
              ({ jointBillServiceId }) =>
                !jointBillServiceIdFilter ||
                jointBillServiceId === jointBillServiceIdFilter,
            )
            .map(({ availableYears }) => availableYears)
            .flat(),
        ),
      ]
        .map((year) => ({
          label: year.toString(),
          value: year,
        }))
        .sort((a, b) => b.value - a.value),
    ];

    if (!yearOptions.length) {
      yearOptions = [
        {
          label: getYear(new Date()).toString(),
          value: getYear(new Date()),
        },
      ];
    }

    const jointBillServiceOptions: {
      label: string;
      value: number;
    }[] = overviewData.jointBillServices.map((jbs) => ({
      label: jbs.address ?? jbs.displayName,
      value: jbs.jointBillServiceId,
    }));

    if (yearOptions.length) {
      set({ yearFilter: yearOptions[0]!.value });
    }

    if (!jointBillServiceIdFilter && jointBillServiceOptions.length) {
      set({ jointBillServiceIdFilter: jointBillServiceOptions[0]!.value });
    }

    set({ yearOptions, jointBillServiceOptions });
  },
  clear: () => set(initialStore),
}));
